import { useTranslation } from "react-i18next";
import React, { useState } from 'react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity } from './GranularitySelector';
import { DisputesFilters } from './DisputesFilters';
import { EmptyState } from './EmptyState';
import { ChevronDownIcon } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DisputesTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function DisputesTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: DisputesTabProps) {
  const {
    t
  } = useTranslation("analytics");
  // New filter states
  const [disputeDateBasis, setDisputeDateBasis] = useState<'dispute-date' | 'payment-date'>('dispute-date');
  const [paymentCountBasis, setPaymentCountBasis] = useState<'payment-count' | 'payment-volume'>('payment-count');
  const [includeEFW, setIncludeEFW] = useState<'include' | 'exclude'>('include');
  // Existing filter states
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDisputeDateBasis('dispute-date');
    setPaymentCountBasis('payment-count');
    setIncludeEFW('include');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-1804">
      {/* All Filters - Compact Layout */}
      <div className="space-y-3" data-id="element-1805">
        {/* First Row: Date Range and Granularity */}
        <div className="flex flex-wrap items-center gap-3" data-id="element-1806">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-1807" />
          <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-1808" />
          
        </div>

        {/* Disputes Filters (includes both rows) */}
        <DisputesFilters disputeDateBasis={disputeDateBasis} onDisputeDateBasisChange={setDisputeDateBasis} paymentCountBasis={paymentCountBasis} onPaymentCountBasisChange={setPaymentCountBasis} includeEFW={includeEFW} onIncludeEFWChange={setIncludeEFW} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-1809" />
        
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1810">
        <div className="p-6 border-b border-gray-200" data-id="element-1811">
          <div className="flex items-center justify-between" data-id="element-1812">
            <div className="flex items-center gap-4" data-id="element-1813">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1814">
                {t("components.DisputesTab.key_metrics_for_cards")}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1815">
                <span data-id="element-1816">{t("components.AuthenticationTab.compared_to")}</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1817">
                  {t("components.AuthenticationTab.previous_period")}
                  <ChevronDownIcon size={16} data-id="element-1818" />
                </button>
                <span className="text-gray-500" data-id="element-1819">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-1820">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1822">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1821" />
          
        </div>
      </div>

      {/* Card disputes breakdown */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1823">
        <div className="p-6 border-b border-gray-200" data-id="element-1824">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1825">
            {t("components.DisputesTab.card_disputes_breakdown")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1826">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1828">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1827" />
          
        </div>
      </div>

      {/* Monthly submission performance */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1829">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1830">
          <div data-id="element-1831">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1832">
              {t("components.DisputesTab.monthly_submission_performance")}
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1833">
              {t("components.DisputesTab.a_detailed_view_of_submission_performance")}
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1834">
            {t("components.DisputesTab.all_reasons")}
            <ChevronDownIcon size={16} data-id="element-1835" />
          </button>
        </div>

        <div className="p-12" data-id="element-1836">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1838">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1837" />
          
        </div>
      </div>

      {/* Monthly dispute outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1839">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1840">
          <div data-id="element-1841">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1842">
              {t("components.DisputesTab.monthly_dispute_outcomes")}
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1843">
              {t("components.DisputesTab.a_breakdown_of_how_card_disputes")}
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1844">
            {t("components.DisputesTab.all_reasons")}
            <ChevronDownIcon size={16} data-id="element-1845" />
          </button>
        </div>

        <div className="p-12" data-id="element-1846">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1848">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1847" />
          
        </div>
      </div>
    </div>;
}